import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { Button, Form, Grid, Header, Image, Icon, Container, Table, Label } from 'semantic-ui-react'
import TopBar from '../../../components/topBar/topBar';
import Breadcrumb from '../../../components/breadcrumb/breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers as fetchUsersAction } from '../../../store/getUsers/action';
import { useHistory } from 'react-router-dom';
import { fetchTenants } from '../../../store/getTenants/action';
import { roleLabels } from '../../../interfaces/roleType';
import { getQuickDiscountValues } from '../../../interfaces/discountValue';
import { ManualDiscountMode, isManualDiscountEnabled } from '../../../interfaces/manualDiscount';

// new users get a 1995 placeholder as lastLoginAt (see api addUser / mongo-init)
const hasLoggedIn = (user: any): boolean =>
    Boolean(user.lastLoginAt) && dayjs(user.lastLoginAt).year() >= 2000

// enabled: solid green with a checkmark, disabled: outlined with an X; "Standard" when following the tenant
const renderManualDiscountLabel = (mode: ManualDiscountMode | undefined, tenantDefault: boolean) => {
    const enabled = isManualDiscountEnabled(mode, tenantDefault)
    const isDefault = mode !== 'on' && mode !== 'off'

    return (
        <Label size='mini' basic={!enabled} color={enabled ? 'green' : undefined} style={{ margin: 2 }}>
            {isDefault ? 'Manuell: Standard' : 'Manuell'}
            <Icon name={enabled ? 'check' : 'close'} style={{ margin: '0 0 0 0.5em' }} />
        </Label>
    )
}

const UserListScreen: React.ComponentType<any> = () => {
    const dispatch = useDispatch(); 
    const history = useHistory();
    const getUsers = useSelector((state: any) => state.getUsers);
    const getTenants = useSelector((state: any) => state.getTenants);

    useEffect(
        () => {
            dispatch(fetchUsersAction());
          return () => {};
        }, []
    );

    useEffect(
        () => {
            dispatch(fetchTenants());
          return () => {};
        }, []
    );

    const renderRow = (user: any) => {
        const findTenantName = getTenants.data ? getTenants.data.find((tenant: any) => tenant._id === user.tenantID) : null
        const tenantManualDiscountDefault = Boolean(findTenantName?.manualDiscountDefault)
        const quickDiscountValues = getQuickDiscountValues(user)
        const manualDiscountEnabled = isManualDiscountEnabled(user.manualDiscount, tenantManualDiscountDefault)
        // a single quick button and no manual input: there is nothing to choose
        const isAutomaticDiscount = quickDiscountValues.length === 1 && !manualDiscountEnabled

        return (
            <Table.Row key={user?._id}>
                <Table.Cell>{user.username}</Table.Cell>
                <Table.Cell>{ findTenantName && findTenantName.name ? findTenantName.name : 'Nicht gefunden' }</Table.Cell>
                <Table.Cell>{roleLabels[user.role] || user.role}</Table.Cell>
                <Table.Cell>
                    {quickDiscountValues.map(({ discountValue, label }, i) => (
                        <Label key={i} size='mini' title={label || undefined} style={{ margin: 2, color: '#fff', backgroundColor: '#346BBB' }}>
                            {`${Number(discountValue).toFixed(2).replace('.', ',')} €`}
                        </Label>
                    ))}
                    {isAutomaticDiscount && (
                        <Label size='mini' basic style={{ margin: 2, color: '#346BBB', borderColor: '#346BBB' }}>
                            <Icon name='bolt' />
                            Direkt
                        </Label>
                    )}
                    {renderManualDiscountLabel(user.manualDiscount, tenantManualDiscountDefault)}
                </Table.Cell>
                <Table.Cell>{hasLoggedIn(user) ? dayjs(user.lastLoginAt).format('DD.MM.YYYY HH:mm:ss') : ''}</Table.Cell>
                <Table.Cell>
                    <div 
                        style={{ cursor: 'pointer' }}
                        onClick={() => history.push({pathname: '/admin/users/edit', state: { user: user } })}
                    >
                        <Icon name='pencil' />
                    </div>
                </Table.Cell>
            </Table.Row>
        )
    }

    return (
        <div>
            <TopBar />
            <Container>
                <Breadcrumb isAddUserButton />
                <Form loading={getUsers.pending}>
                    <Table celled selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Benutzername</Table.HeaderCell>
                                <Table.HeaderCell>Mandant</Table.HeaderCell>
                                <Table.HeaderCell>Benutzerrolle</Table.HeaderCell>
                                <Table.HeaderCell>Befreiungs-Beträge</Table.HeaderCell>
                                <Table.HeaderCell>Letzte Anmeldung</Table.HeaderCell>
                                <Table.HeaderCell>Bearbeiten</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {
                                getUsers.data && getUsers.data.map((user: any) => renderRow(user))
                            }
                        </Table.Body>
                    </Table>
                </Form>
            </Container>
        </div>
    )
}

export default UserListScreen;